/**
 * View Outputs page -- production results with auto-refresh
 */

import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Loader2,
  Download,
  Video,
  CheckCircle2,
  XCircle,
  Clock,
  Package,
  RotateCcw,
  Share2,
  Link,
  Trash2,
} from "lucide-react";
import { getEvent, getProductions, retryProduction, deleteProduction } from "@/lib/api";
import type { MixProduction } from "@/lib/store";
import { cn, formatDate } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

const statusConfig: Record<
  string,
  {
    label: string;
    variant: "secondary" | "success" | "warning" | "default" | "destructive";
    icon: typeof Clock;
  }
> = {
  pending: { label: "Pending", variant: "secondary", icon: Clock },
  queued: { label: "Queued", variant: "default", icon: Clock },
  processing: { label: "Processing", variant: "warning", icon: Loader2 },
  completed: { label: "Completed", variant: "success", icon: CheckCircle2 },
  failed: { label: "Failed", variant: "destructive", icon: XCircle },
};

function isVerticalStyle(style: string): boolean {
  return ["tiktok", "instagram", "youtube_short"].includes(style);
}

export function ViewOutputs() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MixProduction | null>(null);

  const retryMutation = useMutation({
    mutationFn: (prodId: string) => retryProduction(id!, prodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["productions", id] });
      setRetryingId(null);
      setRetryError(null);
    },
    onError: (err: Error) => {
      setRetryingId(null);
      setRetryError(err.message || "Retry failed. Please try again.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (prodId: string) => deleteProduction(id!, prodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["productions", id] });
      setDeleteTarget(null);
    },
  });

  const { data: event } = useQuery({
    queryKey: ["event", id],
    queryFn: () => getEvent(id!),
    enabled: !!id,
  });

  const { data: productions, isLoading } = useQuery({
    queryKey: ["productions", id],
    queryFn: () => getProductions(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      // Auto-refresh while any production is incomplete
      const prods = query.state.data as MixProduction[] | undefined;
      if (!prods) return false;
      const anyIncomplete = prods.some(
        (p) => p.status !== "completed" && p.status !== "failed"
      );
      return anyIncomplete ? 10_000 : false;
    },
  });

  const completedCount =
    productions?.filter((p: MixProduction) => p.status === "completed")
      .length ?? 0;
  const failedCount =
    productions?.filter((p: MixProduction) => p.status === "failed")
      .length ?? 0;
  const totalCount = productions?.length ?? 0;
  const progressPercent =
    totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const allDone = totalCount > 0 && completedCount + failedCount === totalCount;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/events/${id}`)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-vizimix-100">Outputs</h2>
            <p className="text-sm text-vizimix-400 mt-1">
              {event?.title} -- {completedCount}/{totalCount} completed
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/events/${id}/review`)}
            className="gap-1"
          >
            <Video className="h-3 w-3" />
            Review
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/events/${id}/produce`)}
            className="gap-1"
          >
            <Package className="h-3 w-3" />
            Produce
          </Button>
        </div>
      </div>

      {/* Retry Error Banner */}
      {retryError && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg flex items-center justify-between">
          <span className="text-sm text-destructive">{retryError}</span>
          <Button variant="ghost" size="sm" onClick={() => setRetryError(null)} className="text-red-400 h-7 px-2">
            Dismiss
          </Button>
        </div>
      )}

      {/* Progress Bar */}
      {productions && productions.length > 0 && (
        <div className="mb-6 p-4 bg-organize-card border border-organize-border rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-vizimix-200">
              Production Progress
            </span>
            <span className="text-sm text-vizimix-400">
              {completedCount}/{totalCount} completed
              {failedCount > 0 && (
                <span className="text-red-400 ml-2">
                  ({failedCount} failed)
                </span>
              )}
            </span>
          </div>
          <div className="h-2 w-full bg-organize-bg rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                allDone && failedCount === 0
                  ? "bg-emerald-500"
                  : failedCount > 0 && allDone
                    ? "bg-amber-500"
                    : "bg-vizimix-500"
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <p className="text-xs mt-2 text-vizimix-400">
            {allDone && failedCount === 0 ? (
              <span className="text-emerald-400 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" />
                All videos ready!
              </span>
            ) : allDone && failedCount > 0 ? (
              <span className="text-amber-400">
                Completed with {failedCount} failure{failedCount !== 1 ? "s" : ""}
              </span>
            ) : (
              <span className="animate-pulse">Processing...</span>
            )}
          </p>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-vizimix-500" />
        </div>
      )}

      {/* Empty */}
      {productions && productions.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Video className="h-12 w-12 text-vizimix-600 mb-4" />
            <h3 className="text-lg font-semibold text-vizimix-200 mb-2">
              No productions yet
            </h3>
            <p className="text-sm text-vizimix-400 mb-6 text-center">
              Start a batch production from the event detail page.
            </p>
            <Button
              variant="outline"
              onClick={() => navigate(`/events/${id}/produce`)}
            >
              Configure Production
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Productions Grid */}
      {productions && productions.length > 0 && (
        <div className={cn(
          "grid gap-4",
          productions.every((p: MixProduction) => isVerticalStyle(p.style))
            ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-4xl"
            : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
        )}>
          {productions.map((prod: MixProduction) => {
            const config = statusConfig[prod.status] || statusConfig.pending;
            const StatusIcon = config.icon;

            return (
              <Card key={prod.id} className="overflow-hidden">
                {/* Video preview / placeholder */}
                <div className={cn(
                  "relative bg-organize-bg flex items-center justify-center",
                  isVerticalStyle(prod.style) ? "aspect-[9/16] max-h-96 mx-auto" : "aspect-video"
                )}>
                  {prod.status === "completed" && prod.jobId ? (
                    <video
                      controls
                      preload="metadata"
                      poster={`/api/events/${id}/productions/${prod.id}/poster`}
                      className="w-full h-full"
                      src={`/api/events/${id}/productions/${prod.id}/download`}
                    >
                      <track kind="captions" />
                    </video>
                  ) : prod.status === "processing" ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-8 w-8 animate-spin text-vizimix-500" />
                      <span className="text-xs text-vizimix-400">
                        Processing...
                      </span>
                    </div>
                  ) : prod.status === "failed" ? (
                    <div className="flex flex-col items-center gap-2">
                      <XCircle className="h-8 w-8 text-red-400" />
                      <span className="text-xs text-red-400">
                        Production failed
                      </span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Clock className="h-8 w-8 text-vizimix-600" />
                      <span className="text-xs text-vizimix-400">
                        Waiting...
                      </span>
                    </div>
                  )}
                </div>

                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-vizimix-200 capitalize">
                        {prod.style.replace("_", " ")}
                      </span>
                      <span className="text-xs text-vizimix-500">
                        #{prod.sequenceIndex + 1}
                      </span>
                    </div>
                    <Badge variant={config.variant} className="gap-1">
                      <StatusIcon
                        className={`h-3 w-3 ${prod.status === "processing" ? "animate-spin" : ""}`}
                      />
                      {config.label}
                    </Badge>
                  </div>

                  {prod.creditsCharged > 0 && (
                    <p className="text-xs text-vizimix-400">
                      {prod.creditsCharged} credits charged
                    </p>
                  )}

                  <p className="text-xs text-vizimix-500">
                    Created {formatDate(prod.createdAt)}
                    {prod.completedAt && (
                      <> -- Completed {formatDate(prod.completedAt)}</>
                    )}
                  </p>

                  {prod.customPrompt && (
                    <p className="text-xs text-vizimix-400 italic line-clamp-2">
                      {prod.customPrompt}
                    </p>
                  )}

                  {/* Actions */}
                  {prod.status === "completed" && prod.jobId && (
                    <div className="flex flex-wrap gap-2 pt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 gap-1"
                        onClick={() =>
                          window.open(
                            `/api/events/${id}/productions/${prod.id}/download`,
                            "_blank"
                          )
                        }
                      >
                        <Download className="h-3 w-3" />
                        Download
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1"
                        disabled={retryMutation.isPending && retryingId === prod.id}
                        onClick={() => {
                          setRetryingId(prod.id);
                          retryMutation.mutate(prod.id);
                        }}
                        title="Rerun production"
                      >
                        {retryMutation.isPending && retryingId === prod.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <RotateCcw className="h-3 w-3" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1"
                        title="Copy link"
                        onClick={() => {
                          const url = `${window.location.origin}/api/events/${id}/productions/${prod.id}/download`;
                          navigator.clipboard.writeText(url);
                          setCopiedId(prod.id);
                          setTimeout(() => setCopiedId(null), 2000);
                        }}
                      >
                        {copiedId === prod.id ? (
                          <CheckCircle2 className="h-3 w-3 text-green-400" />
                        ) : (
                          <Link className="h-3 w-3" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1"
                        title="Share"
                        onClick={async () => {
                          const downloadUrl = `/api/events/${id}/productions/${prod.id}/download`;
                          const fullUrl = `${window.location.origin}${downloadUrl}`;
                          const title = `${event?.title ?? "ViziMix"} - ${prod.style.replace("_", " ")}`;

                          // Try native share with file, then URL, then clipboard fallback
                          if ("share" in navigator) {
                            try {
                              const res = await fetch(downloadUrl);
                              const blob = await res.blob();
                              const file = new File(
                                [blob],
                                `${event?.title ?? "video"}-${prod.style}-${prod.sequenceIndex + 1}.mp4`,
                                { type: blob.type }
                              );
                              if (navigator.canShare?.({ files: [file] })) {
                                await navigator.share({ title, files: [file] });
                                return;
                              }
                            } catch {
                              // File share failed — try URL share
                            }
                            try {
                              await navigator.share({ title, url: fullUrl });
                              return;
                            } catch {
                              // URL share failed or cancelled — fall through to clipboard
                            }
                          }

                          // Clipboard fallback for browsers without share support
                          try {
                            await navigator.clipboard.writeText(fullUrl);
                            setCopiedId(prod.id);
                            setTimeout(() => setCopiedId(null), 2000);
                          } catch {
                            // Clipboard also failed — open in new tab as last resort
                            window.open(fullUrl, "_blank");
                          }
                        }}
                      >
                        <Share2 className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                        onClick={() => setDeleteTarget(prod)}
                        title="Delete production"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                  {prod.status === "failed" && (
                    <div className="flex gap-2 pt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 gap-1"
                        disabled={retryMutation.isPending && retryingId === prod.id}
                        onClick={() => {
                          setRetryingId(prod.id);
                          retryMutation.mutate(prod.id);
                        }}
                      >
                        {retryMutation.isPending && retryingId === prod.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <RotateCcw className="h-3 w-3" />
                        )}
                        Retry
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                        onClick={() => setDeleteTarget(prod)}
                        title="Delete production"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title="Delete Production"
        description={`Are you sure you want to delete this ${deleteTarget?.style?.replace("_", " ") ?? ""} production? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmVariant="destructive"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
